import React, { useRef, useState } from 'react';
import { Avatar, Field, Icon } from '@/components/ui';
import { LeadOption } from './createAccount.helpers';

interface LeadSelectProps {
  value: LeadOption | null;
  options: LeadOption[];
  loading: boolean;
  disabled?: boolean;
  onOpen: () => void;
  onSelect: (option: LeadOption) => void;
}

export const LeadSelect = ({
  value,
  options,
  loading,
  disabled,
  onOpen,
  onSelect,
}: LeadSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const opened = useRef(false);

  if (disabled) {
    return (
      <div className="tw-flex tw-items-center tw-gap-[8px] tw-h-[40px] tw-px-[14px] tw-rounded-full tw-bg-ds-field-disabled tw-font-poppins tw-text-[14px] tw-text-ds-storm">
        {value && <Avatar name={value.label} size={22} />}
        <span>{value?.label}</span>
      </div>
    );
  }

  const handleOpen = () => {
    setOpen(true);
    if (!opened.current) {
      opened.current = true;
      onOpen();
    }
  };

  // Show the selected label in the closed state; switch to the search box when open.
  if (!open && value) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className="tw-flex tw-items-center tw-gap-[8px] tw-w-full tw-h-[40px] tw-px-[14px] tw-rounded-full tw-bg-white tw-border tw-border-ds-hairline tw-cursor-pointer tw-font-poppins tw-text-[14px] tw-text-ds-storm"
      >
        <Avatar name={value.label} size={22} />
        <span className="tw-flex-1 tw-text-left tw-truncate">
          {value.label}
        </span>
        <Icon name="chevron-down" size={16} color="var(--ink-mute)" />
      </button>
    );
  }

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-[6px]">
      <div onFocus={handleOpen}>
        <Field
          icon="search"
          placeholder="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery('')}
        />
      </div>
      {open && (
        <div className="tw-max-h-[180px] tw-overflow-y-auto tw-border tw-border-ds-hairline tw-rounded-ds-m tw-bg-white">
          {loading && (
            <div className="tw-px-[14px] tw-py-[12px] tw-font-poppins tw-text-[13px] tw-text-ds-ink-mute">
              Loading…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="tw-px-[14px] tw-py-[12px] tw-font-poppins tw-text-[13px] tw-text-ds-ink-mute">
              No leads found.
            </div>
          )}
          {!loading &&
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onSelect(o);
                  setOpen(false);
                  setQuery('');
                }}
                className="tw-flex tw-items-center tw-gap-[10px] tw-w-full tw-px-[14px] tw-py-[10px] tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-left tw-font-poppins tw-text-[14px] tw-text-ds-storm hover:tw-bg-ds-sky-mist"
              >
                <Avatar name={o.label} size={24} />
                <span className="tw-truncate">{o.label}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};
